import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, User, PhoneCall, CheckCircle } from "lucide-react";

type HistoryRecord = {
  id: number;
  lead_id: string;
  counselor_email: string;
  counselor_name: string;
  status: string;
  remark: string;
  created_at: string;
};

export default function LeadHistory() {
  const { id } = useParams(); // URL se lead ka ID nikalega
  const navigate = useNavigate();
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [id]);

  const fetchHistory = async () => {
    try {
      // const response = await fetch(`http://localhost:9090/api/enquiry/history/${id}`);
      const response = await fetch(`https://node-api.mediitacademy.com/api/enquiry/history/${id}`);
      const data = await response.json();
      if (data.success) {
        setHistory(data.history);
      }
    } catch (error) {
      console.error("Failed to fetch lead history", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Status ke hisaab se color return karne ka logic
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Interested": return "bg-blue-50 text-blue-700 border-blue-200";
      case "Converted": return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Not Interested": return "bg-red-50 text-red-700 border-red-200";
      case "Not Answered": return "bg-orange-50 text-orange-700 border-orange-200";
      case "System assigned": return "bg-slate-100 text-slate-700 border-slate-300";
      default: return "bg-purple-50 text-purple-700 border-purple-200";
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header & Back Button */}
      <div className="mb-8 flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)} 
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-500 shadow-sm hover:bg-slate-50 transition"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-900">Lead Interaction History</h1>
          <p className="text-sm font-semibold text-slate-500">Tracking all calls, assignments, and remarks for <span className="font-bold text-slate-800">{id}</span></p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm min-h-[500px]">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-sm font-semibold text-slate-500">
            Fetching history timeline...
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-60 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <PhoneCall size={40} className="text-slate-300 mb-3" />
            <h3 className="text-lg font-black text-slate-700">No History Found</h3>
            <p className="text-sm font-semibold text-slate-500">No remarks or assignments have been made for this lead yet.</p>
          </div>
        ) : (
          /* Timeline Design */
          <div className="relative border-l-2 border-slate-100 ml-4 sm:ml-6 space-y-10 py-4">
            {history.map((record, index) => (
              <div key={record.id} className="relative pl-6 sm:pl-10">
                {/* Timeline Dot */}
                <div className={`absolute -left-[11px] top-1.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white ${record.status === 'System assigned' ? 'bg-slate-300' : 'bg-blue-500'}`}>
                  {record.status === 'Converted' && <CheckCircle size={10} className="text-white" />}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  {/* Left Side: Remark Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 text-[10px] rounded-lg font-black uppercase tracking-wider border ${getStatusColor(record.status)}`}>
                        {record.status}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md">
                        <Clock size={12} /> {formatDateTime(record.created_at)}
                      </span>
                    </div>
                    
                    <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5 mt-3 shadow-sm">
                      <p className="text-sm font-semibold text-slate-800 leading-relaxed">
                        {record.remark}
                      </p>
                    </div>
                  </div>

                  {/* Right Side: Counselor Details */}
                  <div className="sm:w-64 shrink-0 rounded-2xl bg-white border border-slate-100 p-4 shadow-sm self-start flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white font-black text-sm">
                      {record.counselor_name ? record.counselor_name.charAt(0).toUpperCase() : "A"}
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-0.5">Updated By</p>
                      <p className="text-sm font-black text-slate-900 leading-none">{record.counselor_name || "Admin"}</p>
                      <p className="text-[10px] font-bold text-slate-500 mt-1 truncate max-w-[150px]" title={record.counselor_email}>
                        {record.counselor_email}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}